import React, { useState } from "react";
import { ChevronDownIcon, ChevronUpIcon } from "lucide-react";

interface HistoryChange {
  path: string;
  operation: string;
  oldValue: any;
  newValue: any;
}

interface DocumentHistoryEntryProps {
  entry: {
    type: "extracted" | "manual";
    source?: string;
    date: string;
    description: string;
    changes?: HistoryChange[];
  };
}

const formatPathSegment = (segment: string) => {
  // Handle array indices
  if (!isNaN(Number(segment))) {
    return `Item ${Number(segment) + 1}`;
  }

  // Split camelCase and capitalize first letter
  return segment
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (str) => str.toUpperCase())
    .trim();
};

const formatValue = (value: any) => {
  if (value === null || value === undefined) {
    return "<empty>";
  }
  return JSON.stringify(value);
};

export const DocumentHistoryEntry: React.FC<DocumentHistoryEntryProps> = ({ entry }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const changesCount = entry.changes?.length || 0;
  const displayChanges = isExpanded ? entry.changes : entry.changes?.slice(0, 2);

  return (
    <div className={`border-l-2 ${entry.type === "extracted" ? "border-blue-500" : "border-green-500"} pl-3 py-1`}>
      <div className="flex justify-between">
        <div className="flex items-center gap-2">
          <span className="font-medium">{entry.type === "extracted" ? "Extracted" : "Manual"}</span>
          {entry.source && (
            <span className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">
              {entry.source === "email" ? "Email" : "Manual"}
            </span>
          )}
          {changesCount > 0 && (
            <span className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">
              {changesCount} change{changesCount !== 1 ? "s" : ""}
            </span>
          )}
        </div>
        <span className="text-gray-500 text-xs">
          {new Date(entry.date).toLocaleDateString()} {new Date(entry.date).toLocaleTimeString()}
        </span>
      </div>
      <p className="text-xs text-gray-600">{entry.description}</p>

      {entry.changes && entry.changes.length > 0 && (
        <div className="mt-1 space-y-1">
          {displayChanges?.map((change, i) => {
            const formattedPath = change.path.split(".").map(formatPathSegment).join(" › ");

            return (
              <div key={i} className="text-xs bg-gray-50 p-2 rounded-md">
                <span className="font-medium text-gray-700">{formattedPath}</span>
                <span className="text-gray-500 ml-2">
                  {change.operation === "update" && (
                    <>
                      changed from <code className="px-1 bg-gray-100 rounded">{formatValue(change.oldValue)}</code>
                      {" → "}
                      <code className="px-1 bg-gray-100 rounded">{formatValue(change.newValue)}</code>
                    </>
                  )}
                  {change.operation === "add" && (
                    <>
                      added: <code className="px-1 bg-gray-100 rounded">{formatValue(change.newValue)}</code>
                    </>
                  )}
                  {change.operation === "remove" && (
                    <>
                      removed: <code className="px-1 bg-gray-100 rounded">{formatValue(change.oldValue)}</code>
                    </>
                  )}
                </span>
              </div>
            );
          })}

          {changesCount > 2 && (
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="text-xs text-blue-500 hover:text-blue-600 mt-1 flex items-center gap-1"
            >
              {isExpanded ? (
                <>
                  Show less <ChevronUpIcon className="h-3 w-3" />
                </>
              ) : (
                <>
                  Show {changesCount - 2} more changes <ChevronDownIcon className="h-3 w-3" />
                </>
              )}
            </button>
          )}
        </div>
      )}
    </div>
  );
};